import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';

import { AccountingDashboardActionsHelperService } from '../../services/accounting-dashboard-actions-helper.service';
import { AccountingDashboardViewSettingsService } from '../../services/accounting-dashboard-view-settings.service';

@Component({
  selector: 'lum-accounting',
  templateUrl: './accounting.component.html',
  styleUrls: ['./accounting.component.scss'],
})
export class AccountingComponent implements OnInit, OnDestroy {
  submittingForm :boolean = false;
  form :UntypedFormGroup;

  private onDestroy$ = new Subject();

  readonly DUMMY_CHANNELS = [
    {
      name: 'Shopify',
      revenue: 600000,
      sales: 15000,
      units: 30000,
      aov: 600000 / 15000,
      percentage: (600000 / 1000000) * 100,
      isHighlighted: false,
    },
    {
      name: 'Amazon',
      revenue: 250000,
      sales: 9615,
      units: 19230,
      aov: 250000 / 9615,
      percentage: (250000 / 1000000) * 100,
      isHighlighted: false,
    },
    {
      name: 'B2B/Wholesale',
      revenue: 100000,
      sales: 3704,
      units: 7408,
      aov: 100000 / 3704,
      percentage: (100000 / 1000000) * 100,
      isHighlighted: false,
    },
    {
      name: 'Other',
      revenue: 50000,
      sales: 1852,
      units: 3704,
      aov: 50000 / 1852,
      percentage: (50000 / 1000000) * 100,
      isHighlighted: false,
    },
  ];

  readonly CHART_COLORS = [
    'var(--col-green-500)',
    'var(--col-yellow-500)',
    'var(--col-purple-500)',
    'var(--col-cloud-500)',
    'var(--col-blue-500)',
    'var(--col-red-500)',
    'var(--col-orange-500)',
  ];

  constructor(
    private _actions :AccountingDashboardActionsHelperService,
    private _feature :FeatureToggleService,
    private _viewSettings :AccountingDashboardViewSettingsService,
    private fb :UntypedFormBuilder,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.#loadViewSettings();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  initForm() :void {
    this.form = this.fb.group(
      {
        fromDate: [moment().format('YYYY-MM-DD'), Validators.required],
        endDate: [moment().format('YYYY-MM-DD'), Validators.required],
      },
      { validators: this.withinThreeMonthsValidator },
    );
  }

  onSubmit() :void {
    this.submittingForm = true;

    const { fromDate, endDate } = this.form.value;
    const params = {
      fromDate: moment(fromDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    };

    const _onComplete = () => {
      this.submittingForm = false;
      this.cd.detectChanges();
    };

    this._actions.exportJournal(params).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: _onComplete,
      error: _onComplete,
    });
  }

  #loadViewSettings() :void {
    if (!this._feature.isActive('utah-UTAH-363-accounting_dashboard')) return;

    const { fromDate, endDate } = this._viewSettings.getState();

    if (fromDate && endDate) {
      this.form.patchValue({ fromDate, endDate });
    }

    // Subscribe to form changes to save settings
    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((values) => {
      if (values.fromDate && values.endDate) {
        this._viewSettings.updateState({
          fromDate: values.fromDate,
          endDate: values.endDate,
        });
      }
    });
  }

  private withinThreeMonthsValidator(
    control :AbstractControl,
  ) :ValidationErrors | null {
    const fromDateValue = control.get('fromDate')?.value;
    const endDateValue = control.get('endDate')?.value;

    if (!fromDateValue || !endDateValue) {
      return null;
    }

    const fromDate = moment(fromDateValue, 'YYYY-MM-DD');
    const endDate = moment(endDateValue, 'YYYY-MM-DD');
    const threeMonthsLater = fromDate.clone().add(3, 'months');

    if (endDate.isAfter(threeMonthsLater) || fromDate.isAfter(endDate)) {
      return { exceedsThreeMonths: true };
    }

    return null;
  }
}
